/**
 * FIManagerPieChart Component
 *
 * Donut chart showing back-end gross distribution among F&I managers.
 * Caps to top 8 + "Others" bucket (mirrors SalespersonPieChart).
 */

import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { FiManager } from './types';

interface FIManagerPieChartProps {
  fiManagers: FiManager[];
  totalBackEndGross: number;
}

const COLORS = [
  '#2563eb', // blue-600
  '#3b82f6', // blue-500
  '#60a5fa', // blue-400
  '#93c5fd', // blue-300
  '#f97316', // orange-500
  '#fb923c', // orange-400
  '#fdba74', // orange-300
  '#10b981', // emerald-500
];

function formatCurrency(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1 truncate">{data.name}</p>
        <p className="text-sm text-gray-600">
          Back End Gross:{' '}
          <span className="font-medium text-blue-600">
            {formatCurrency(data.value)}
          </span>
        </p>
        <p className="text-sm text-gray-600">
          Share: <span className="font-medium">{data.percentage.toFixed(1)}%</span>
        </p>
        <p className="text-sm text-gray-600">
          Units: <span className="font-medium">{data.units}</span>
        </p>
      </div>
    );
  }
  return null;
};

const renderLegendText = (value: string) => {
  const maxLength = 15;
  if (value.length > maxLength) return value.substring(0, maxLength) + '...';
  return value;
};

export const FIManagerPieChart: React.FC<FIManagerPieChartProps> = ({
  fiManagers,
  totalBackEndGross,
}) => {
  const chartData = useMemo(() => {
    const MAX_SLICES = 8;

    if (fiManagers.length === 0 || totalBackEndGross === 0) {
      return [];
    }

    const sorted = [...fiManagers].sort((a, b) => b.backEndGross - a.backEndGross);

    const toSlice = (mgr: FiManager, index: number) => ({
      name: mgr.fiManagerName,
      value: mgr.backEndGross,
      percentage: (mgr.backEndGross / totalBackEndGross) * 100,
      units: mgr.totalUnits,
      color: COLORS[index % COLORS.length],
    });

    if (sorted.length <= MAX_SLICES) {
      return sorted.map(toSlice);
    }

    const top = sorted.slice(0, MAX_SLICES - 1);
    const others = sorted.slice(MAX_SLICES - 1);
    const othersTotal = others.reduce((sum, mgr) => sum + mgr.backEndGross, 0);
    const othersUnits = others.reduce((sum, mgr) => sum + mgr.totalUnits, 0);

    const result = top.map(toSlice);

    if (othersTotal > 0) {
      result.push({
        name: `Others (${others.length})`,
        value: othersTotal,
        percentage: (othersTotal / totalBackEndGross) * 100,
        units: othersUnits,
        color: '#9ca3af',
      });
    }

    return result;
  }, [fiManagers, totalBackEndGross]);

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
        No data to display
      </div>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={75}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            formatter={renderLegendText}
            wrapperStyle={{ fontSize: '12px', paddingLeft: '10px' }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
